import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { Range } from 'react-range';
import styled, { css } from 'styled-components';

import ValidationMessage from '@/components/Forms/ValidationMessage';
import Tooltip from '@/components/InfoTooltip';
import Text from '@/components/Text';

interface TrackProps {
  $highlight?: {
    position: 'left' | 'right';
    widthPercentage: number;
  };
}

const Track = styled.div<TrackProps>`
  margin-bottom: 13px;
  border-radius: 4px;
  height: 4px;
  background: ${({ theme }) => theme.colors.lightGray};

  ${({ $highlight }) =>
    $highlight &&
    css`
      &::before {
        content: '';
        position: absolute;
        height: 100%;
        border-radius: 4px;
        background: #fbb5a9;
        width: ${$highlight.widthPercentage}%;
        ${$highlight.position === 'left'
          ? css`
              left: 0;
            `
          : css`
              right: 0;
            `};
      }
    `}
`;

const HandleTooltip = styled.div<{ $danger: boolean }>`
  position: absolute;
  display: none;
  bottom: calc(100% + 10px);
  height: 20px;
  width: ${({ theme, $danger }) => ($danger ? 75 : 29)}px;
  left: ${({ theme, $danger }) => ($danger ? -25 : -1)}px;
  right: 0;
  margin: 0 auto;
  border-radius: 4px;
  background: ${({ theme, $danger }) =>
    theme.colors[$danger ? 'error' : 'lightGray']};
  color: ${({ theme, $danger }) =>
    theme.colors[$danger ? 'error' : 'lightGray']};
  align-items: center;
  justify-content: center;
  svg {
    position: absolute;
    bottom: -3px;
    left: 0;
    right: 0;
    margin: 0 auto;
  }
  ${({ theme, $danger }) =>
    $danger &&
    css`
      ${Text} {
        color: ${theme.colors.white};
      }
    `}
`;

const Handle = styled.div<{ $isDragged: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.purple};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  border: 1px solid ${({ theme }) => theme.colors.purple};
  transition: 0.15s ease-out border-color;

  &:hover {
    ${HandleTooltip} {
      display: flex;
    }
  }

  ${({ theme, $isDragged }) =>
    $isDragged &&
    css`
      border-color: ${theme.colors.white};
      ${HandleTooltip} {
        display: flex;
      }
    `}
`;

const Label = styled(Text)`
  margin-bottom: 20px;
`;

const Descriptions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PointsLabel = styled(Text)`
  display: inline-flex;
  align-items: center;
`;

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  minValue: number;
  maxValue: number;
  step: number;
  label: string;
  lowPointLabel: string;
  lowPointDescription?: string;
  hightPointLabel: string;
  highPointDescription?: string;
  trackHighlight?: {
    position: 'left' | 'right';
    widthPercentage: number;
  };
}

const RangeField = <T extends FieldValues>({
  control,
  name,
  minValue,
  maxValue,
  step,
  label,
  lowPointLabel,
  hightPointLabel,
  trackHighlight,
  lowPointDescription,
  highPointDescription,
}: Props<T>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<T>({
    control,
    name,
  });

  const isDanger = (() => {
    if (!trackHighlight) {
      return false;
    }

    if (
      trackHighlight.position === 'left' &&
      value * 100 < trackHighlight.widthPercentage
    ) {
      return true;
    }

    if (
      trackHighlight.position === 'right' &&
      value * 100 > trackHighlight.widthPercentage
    ) {
      return true;
    }

    return false;
  })();

  return (
    <div>
      <Label $type="smallBold">{label}</Label>
      <Range
        step={step}
        min={minValue}
        max={maxValue}
        values={[value * 100]}
        onChange={(values) => onChange(values[0] / 100)}
        renderTrack={({ props, children }) => (
          <Track {...props} $highlight={trackHighlight}>
            {children}
          </Track>
        )}
        renderThumb={({ props, isDragged }) => (
          <Handle {...props} $isDragged={isDragged}>
            <HandleTooltip $danger={isDanger}>
              <Text as="span" $type="extraSmall">
                {Math.round(value * 100)}%{isDanger && ' - Unstable'}
              </Text>
              <svg
                width="6"
                height="6"
                viewBox="0 0 6 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="2.82837"
                  width="4"
                  height="4"
                  transform="rotate(-45 0 2.82837)"
                  fill="currentColor"
                />
              </svg>
            </HandleTooltip>
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5 8L0 4L5 0V8ZM7 8L12 4L7 0V8Z"
                fill="currentColor"
              />
            </svg>
          </Handle>
        )}
      />
      <Descriptions>
        <PointsLabel $type="extraSmall" $color="gray">
          {lowPointLabel}
          {!!lowPointDescription && <Tooltip content={lowPointDescription} />}
        </PointsLabel>
        <PointsLabel $type="extraSmall" $color="gray">
          {hightPointLabel}
          {!!highPointDescription && (
            <Tooltip content={highPointDescription} position="bottom-right" />
          )}
        </PointsLabel>
      </Descriptions>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </div>
  );
};

export default RangeField;
